/**
 * Cart drawer addons: group addon line items (Gift Box, Gift Note, Premium Printing)
 * under their parent product line and manage their lifecycle.
 *
 * - Addons are linked to the parent via the `_GroupKey` line-item property.
 * - Removing an addon keeps the product but clears its flag property
 *   (e.g. "Gift Note: Yes") on the product line.
 * - Removing the product auto-removes all of its linked addons.
 */
(function () {
  'use strict';

  // The AJAX cart API only exists at the store root, not under locale prefixes
  const CHANGE_URL = '/cart/change.js';
  const CART_URL = '/cart.js';

  // Addon type -> properties to clear on the parent line when the addon is removed
  const PROPS_TO_CLEAR = {
    'gift-box': ['Gift Box'],
    gift: ['Gift Box'],
    'gift-note': ['Gift Note', 'Gift Message'],
    'premium-printing': ['Premium Printing', '_PrintingProfile', '_PremiumPrintingGroup'],
  };

  function parseHtml(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  function applySectionHtml(drawerHtml, bubbleHtml) {
    const drawerInner = document.querySelector('cart-drawer .drawer__inner');
    if (drawerInner && drawerHtml) {
      const fresh = parseHtml(drawerHtml).querySelector('.drawer__inner');
      if (fresh) drawerInner.innerHTML = fresh.innerHTML;
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && bubbleHtml) {
      const fresh = parseHtml(bubbleHtml).querySelector('.shopify-section');
      if (fresh) bubble.innerHTML = fresh.innerHTML;
    }

    document.dispatchEvent(
      new CustomEvent('cart:refreshed', { bubbles: true, detail: { source: 'cart-addons' } })
    );
  }

  /**
   * Fallback only: re-fetch rendered sections.
   * The primary path gets fresh HTML from the change.js POST response itself
   * (the `sections` body param), which can never be stale.
   */
  async function rerenderCartSections() {
    // Cache-buster: the CDN in front of the store may ignore query strings,
    // so also fall back to a POST if the GET returns something unusable.
    const res = await fetch(
      `${window.location.pathname}?sections=${SECTIONS.join(',')}&cb=${Date.now()}`,
      { headers: { Accept: 'application/json' }, cache: 'no-store' }
    );
    const data = await res.json();
    applySectionHtml(data['cart-drawer'], data['cart-icon-bubble']);
  }

  async function fetchCart() {
    const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  /**
   * Perform a cart change and return the freshly rendered sections from the
   * same response — no separate GET, so no possibility of stale HTML.
   */
  async function changeLine(payload) {
    const res = await fetch(CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({}, payload, { sections: SECTIONS })),
    });
    if (!res.ok) return null;
    return res.json().catch(() => null);
  }

  async function applyChanges(changes) {
    let last = null;
    for (const change of changes) {
      const data = await changeLine(change);
      if (data && data.sections) last = data;
    }
    if (last && last.sections && last.sections['cart-drawer']) {
      applySectionHtml(last.sections['cart-drawer'], last.sections['cart-icon-bubble']);
    } else {
      await rerenderCartSections();
    }
  }

  function setLoading(on) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('cart-addons--loading', on);
  }

  /**
   * Resolve the CURRENT line key for an addon. The drawer HTML can be stale
   * (cached render / merged duplicate lines), so never trust the DOM key:
   * match by parent group key + addon type against the fresh cart instead.
   */
  function findAddon(cartItems, groupKey, addonType) {
    return cartItems.find(
      (item) =>
        item.properties._GroupKey === groupKey &&
        (item.properties._AddonType === addonType || item.sku === addonType)
    );
  }

  // --- Remove a single addon (keep the product, clear its flag property) ---
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('.cart-item__addon-remove');
    if (!btn) return;
    event.preventDefault();

    const row = btn.closest('[data-addon-key]');
    if (!row) return;
    const { addonKey, parentKey, addonType, groupKey } = row.dataset;

    setLoading(true);
    try {
      // Always work from the live cart: DOM keys may be stale after merges/changes
      const cart = await fetchCart();
      const parent =
        cart.items.find((item) => item.key === parentKey) ||
        (groupKey ? cart.items.find((item) => item.properties._GroupKey === groupKey) : null);
      const addon = parent ? findAddon(cart.items, parent.properties._GroupKey, addonType) : null;

      const changes = [];
      if (addon) changes.push({ id: addon.key, quantity: 0 });
      if (parent) {
        const props = Object.assign({}, parent.properties);
        (PROPS_TO_CLEAR[addonType] || []).forEach((key) => {
          props[key] = null; // null removes the property in the AJAX cart API
        });
        changes.push({ id: parent.key, quantity: parent.quantity, properties: props });
      }

      if (!changes.length) {
        // Nothing left to change (already removed) — just refresh the UI
        await rerenderCartSections();
        return;
      }

      await applyChanges(changes);
    } catch (err) {
      console.error('[cart-addons] failed to remove addon', err);
    } finally {
      setLoading(false);
    }
  });

  // --- Remove the product: auto-remove all of its linked addons first ---
  // Capture phase so this runs before Dawn's cart-remove-button handler.
  document.addEventListener(
    'click',
    async (event) => {
      const removeButton = event.target.closest('cart-remove-button[data-group-key]');
      if (!removeButton || !removeButton.dataset.groupKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const groupKey = removeButton.dataset.groupKey;
      const domParentKey = removeButton.dataset.parentKey;

      setLoading(true);
      try {
        // Resolve the CURRENT parent + addon keys from the live cart:
        // the drawer HTML may be stale after line merges or other changes.
        const cart = await fetchCart();
        const parent =
          cart.items.find((item) => item.key === domParentKey) ||
          cart.items.find((item) => item.properties._GroupKey === groupKey);
        if (!parent) {
          await rerenderCartSections();
          return;
        }
        const changes = cart.items
          .filter(
            (item) =>
              item.key !== parent.key &&
              item.properties._GroupKey === parent.properties._GroupKey
          )
          .map((item) => ({ id: item.key, quantity: 0 }));
        changes.push({ id: parent.key, quantity: 0 });

        await applyChanges(changes);
      } catch (err) {
        console.error('[cart-addons] failed to remove product with addons', err);
      } finally {
        setLoading(false);
      }
    },
    true
  );
})();
